/** Shared layout for calendar day & week views (Notion-style time grid). */

export const CAL_START_H = 0;
export const CAL_END_H = 23;
export const HOUR_PX = 56;
export const CALENDAR_SNAP_MINUTES = 15;
export const CALENDAR_FALLBACK_DURATION_MINUTES = 55;
/** Short events still need a usable on-screen target in the time grid. */
export const CALENDAR_MIN_RENDERED_DURATION_MINUTES = 26;
const CALENDAR_INITIAL_SCROLL_LEAD_MINUTES = 60;

type ParsedTimeRange = {
  start: string;
  end: string | null;
  startMinutes: number;
  endMinutes: number | null;
};

export type CalendarOverlapPlacement = {
  /** How many already-running goals this one is layered over. */
  depth: number;
  /** Deepest layer in the cluster, so the indent can compress in a narrow column. */
  maxDepth: number;
  /** Position among the goals that start at the same minute at this depth. */
  columnIndex: number;
  columnCount: number;
};

export type CalendarOverlapStyle = {
  left: string;
  right: string;
  width: string;
  zIndex?: number;
};

/** Indent per layer — also the width of the strip left exposed underneath. */
const CALENDAR_CASCADE_STEP_PX = 20;
/** The deepest layer never starts past this much of the column. */
const CALENDAR_CASCADE_MAX_PERCENT = 45;
/** Gap between goals that start at the same minute and share a layer. */
const CALENDAR_COLUMN_GAP_PX = 4;
/** Keeps the cascade under the selected (20) and dragging (40) blocks. */
const CALENDAR_MAX_CASCADE_Z = 18;

export function hourSlotCount(): number {
  return CAL_END_H - CAL_START_H + 1;
}

export function hourLabels(): string[] {
  const out: string[] = [];
  for (let h = CAL_START_H; h <= CAL_END_H; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
  }
  return out;
}

function parseClock(value: string): { normalized: string; minutes: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return {
    normalized: `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`,
    minutes: hh * 60 + mm,
  };
}

export function parseTimeRange(time: string): ParsedTimeRange | null {
  const raw = time.trim();
  if (!raw) return null;

  const normalizedRange = raw.replace(/\u2013/g, "-");
  const [rawStart, rawEnd] = normalizedRange.split("-", 2).map((part) => part.trim());
  const parsedStart = parseClock(rawStart || "");
  if (!parsedStart) return null;

  if (!rawEnd) {
    return {
      start: parsedStart.normalized,
      end: null,
      startMinutes: parsedStart.minutes,
      endMinutes: null,
    };
  }

  const parsedEnd = parseClock(rawEnd);
  if (!parsedEnd) {
    return {
      start: parsedStart.normalized,
      end: null,
      startMinutes: parsedStart.minutes,
      endMinutes: null,
    };
  }

  return {
    start: parsedStart.normalized,
    end: parsedEnd.normalized,
    startMinutes: parsedStart.minutes,
    endMinutes: parsedEnd.minutes,
  };
}

/**
 * Returns the clock minute that should be shown at the top of a fresh
 * calendar timeline. Timed items get a one-hour lead; an empty day uses the
 * user's saved operational day start instead.
 */
export function initialCalendarScrollMinutes(
  items: ReadonlyArray<{ time: string }>,
  dayStartTime: string
): number {
  const earliestStart = items.reduce<number | null>((earliest, item) => {
    const parsed = parseTimeRange(item.time);
    if (!parsed) return earliest;
    return earliest === null ? parsed.startMinutes : Math.min(earliest, parsed.startMinutes);
  }, null);

  if (earliestStart !== null) {
    return Math.max(0, earliestStart - CALENDAR_INITIAL_SCROLL_LEAD_MINUTES);
  }

  return parseTimeRange(dayStartTime)?.startMinutes ?? 0;
}

export function formatTimeLabel(time: string): string {
  const parsed = parseTimeRange(time);
  if (!parsed) return time;
  return parsed.end ? `${parsed.start}–${parsed.end}` : parsed.start;
}

export function formatTimeDuration(time: string): string {
  const parsed = parseTimeRange(time);
  if (parsed?.endMinutes == null || parsed.endMinutes <= parsed.startMinutes) return "";
  const minutes = parsed.endMinutes - parsed.startMinutes;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder}m`;
  if (remainder === 0) return `${hours}h`;
  return `${hours}h ${remainder}m`;
}

export function timeDateTimeValue(time: string): string {
  const parsed = parseTimeRange(time);
  return parsed ? parsed.start : "";
}

/** Minutes from CAL_START_H (00:00) for positioning in the grid. */
export function minutesFromGridStart(time: string): number | null {
  const parsed = parseTimeRange(time);
  if (!parsed) return null;
  const dayM = parsed.startMinutes;
  const startM = CAL_START_H * 60;
  const endM = (CAL_END_H + 1) * 60;
  if (dayM < startM || dayM >= endM) return null;
  return dayM - startM;
}

export function gridHeightPx(): number {
  return hourSlotCount() * HOUR_PX;
}

export function blockStyleFromMinutes(startMinutes: number, endMinutes: number): { top: number; height: number } | null {
  const gridStartM = CAL_START_H * 60;
  const gridEndM = (CAL_END_H + 1) * 60;
  if (startMinutes < gridStartM || startMinutes >= gridEndM) return null;

  const clampedEnd = Math.min(Math.max(endMinutes, startMinutes), gridEndM);
  const durationMins = Math.max(CALENDAR_MIN_RENDERED_DURATION_MINUTES, clampedEnd - startMinutes);
  const top = ((startMinutes - gridStartM) / 60) * HOUR_PX;
  const height = Math.max(24, (durationMins / 60) * HOUR_PX - 2);

  return { top, height };
}

export function blockStyleFromTime(time: string): { top: number; height: number } | null {
  const parsed = parseTimeRange(time);
  if (!parsed) return null;
  const startMinutes = parsed.startMinutes;
  let endMinutes = parsed.endMinutes ?? startMinutes + CALENDAR_FALLBACK_DURATION_MINUTES;
  if (parsed.endMinutes !== null && parsed.endMinutes < startMinutes) {
    // Wrapped range ("ends the next day"): this source-day segment runs to
    // midnight. CalendarDayView/CalendarWeekView render the 00:00-end
    // continuation as a separate block on the following day.
    endMinutes = (CAL_END_H + 1) * 60;
  } else if (endMinutes <= startMinutes) {
    endMinutes = startMinutes + CALENDAR_FALLBACK_DURATION_MINUTES;
  }
  return blockStyleFromMinutes(startMinutes, endMinutes);
}

/** True when `time` is "HH:MM-HH:MM" with an end strictly before its start --
 * the wrap-flag convention for "ends the next day". */
export function isWrappedTimeRange(time: string): boolean {
  const parsed = parseTimeRange(time);
  return Boolean(
    parsed && parsed.endMinutes !== null && parsed.endMinutes < parsed.startMinutes
  );
}

/**
 * Layout for the second visual segment of a wrapped item -- the 00:00-end
 * continuation rendered on the day after `time`'s source day. Returns null for
 * a non-wrapped (or open-ended) range.
 */
export function blockStyleForWrapContinuation(
  time: string
): { top: number; height: number } | null {
  const parsed = parseTimeRange(time);
  if (!parsed || parsed.endMinutes === null || parsed.endMinutes >= parsed.startMinutes) {
    return null;
  }
  return blockStyleFromMinutes(0, parsed.endMinutes);
}

export function layoutCalendarOverlaps(items: Array<{ id: string; time: string }>): Map<string, CalendarOverlapPlacement> {
  const gridEndMinutes = (CAL_END_H + 1) * 60;
  const intervals = items
    .map((item) => {
      const parsed = parseTimeRange(item.time);
      if (!parsed) return null;
      const actualEndMinutes = Math.min(
        parsed.endMinutes && parsed.endMinutes > parsed.startMinutes ? parsed.endMinutes : parsed.startMinutes + CALENDAR_FALLBACK_DURATION_MINUTES,
        gridEndMinutes
      );
      // Match the visual extent produced by blockStyleFromMinutes. Without this,
      // a short event can visually cover a later event that the layout considers
      // non-overlapping, leaving the short event impossible to select.
      const endMinutes = Math.max(actualEndMinutes, parsed.startMinutes + CALENDAR_MIN_RENDERED_DURATION_MINUTES);
      return { id: item.id, startMinutes: parsed.startMinutes, endMinutes };
    })
    .filter((item): item is { id: string; startMinutes: number; endMinutes: number } => item !== null)
    // Longest first among goals that start together, so the one that spans the
    // most time becomes the base the shorter ones are layered over.
    .sort((first, second) => first.startMinutes - second.startMinutes || second.endMinutes - first.endMinutes);

  const placements = new Map<string, CalendarOverlapPlacement>();
  let active: Array<{ endMinutes: number }> = [];
  let cluster: Array<{
    id: string;
    startMinutes: number;
    endMinutes: number;
    depth: number;
  }> = [];

  function finishCluster() {
    if (!cluster.length) return;
    let maxDepth = 0;
    const columnCounts = new Map<string, number>();
    for (const entry of cluster) {
      maxDepth = Math.max(maxDepth, entry.depth);
      const key = `${entry.depth}:${entry.startMinutes}`;
      columnCounts.set(key, (columnCounts.get(key) ?? 0) + 1);
    }
    for (const entry of cluster) {
      const placement = placements.get(entry.id);
      if (!placement) continue;
      placement.maxDepth = maxDepth;
      placement.columnCount =
        columnCounts.get(`${entry.depth}:${entry.startMinutes}`) ?? 1;
    }
    cluster = [];
  }

  for (const interval of intervals) {
    active = active.filter((entry) => entry.endMinutes > interval.startMinutes);
    if (active.length === 0 && cluster.length > 0) finishCluster();

    // Only the goals still on screen when this one starts can layer under it.
    const running = cluster.filter(
      (entry) => entry.endMinutes > interval.startMinutes
    );
    const sameStart = running.filter(
      (entry) => entry.startMinutes === interval.startMinutes
    );
    const earlier = running.filter(
      (entry) => entry.startMinutes < interval.startMinutes
    );
    // Goals that start together share a layer and split it side by side;
    // a goal that starts later sits one layer above everything it covers, so
    // an earlier goal is never hidden behind a block of equal indent.
    const depth = sameStart.length
      ? sameStart[0].depth
      : earlier.length
        ? Math.max(...earlier.map((entry) => entry.depth)) + 1
        : 0;

    active.push({ endMinutes: interval.endMinutes });
    cluster.push({ ...interval, depth });
    placements.set(interval.id, {
      depth,
      maxDepth: depth,
      columnIndex: sameStart.length,
      columnCount: 1,
    });
  }
  finishCluster();
  return placements;
}

/**
 * Notion-style layering: a goal keeps the full width of its column, and a goal
 * that starts while it is running is drawn one step in from the left on top of
 * it, leaving the strip underneath exposed and clickable. Goals that start at
 * the same minute share a layer and split it side by side instead of hiding
 * each other. Equal-width tiling was the alternative and it reads badly: a
 * long goal crossing a busy morning would be squeezed to a sliver by short
 * goals it merely contains.
 *
 * Every block still stops short of `--cal-create-strip`, so there is always
 * bare grid on the right of a column to click-drag a new goal into.
 */
export function overlapStyleForPlacement({
  depth,
  maxDepth,
  columnIndex,
  columnCount,
}: CalendarOverlapPlacement): CalendarOverlapStyle {
  const inset = "var(--cal-block-inset, 10px)";
  const strip = "var(--cal-create-strip, 24px)";
  // The pixel step is what a pointer needs to hit the goal underneath; the
  // percentage caps it so a deep stack in a narrow week column compresses
  // instead of pushing the top goal off the right edge. Because the cap is
  // proportional to depth, two layers never collapse onto the same offset.
  const indent =
    depth > 0
      ? `min(${depth * CALENDAR_CASCADE_STEP_PX}px, ${Number(
          ((depth * CALENDAR_CASCADE_MAX_PERCENT) / Math.max(maxDepth, 1)).toFixed(2)
        )}%)`
      : null;
  const zIndex = Math.min(depth + columnIndex + 2, CALENDAR_MAX_CASCADE_Z);

  if (columnCount <= 1) {
    if (!indent) {
      return { left: inset, right: strip, width: "auto" };
    }
    return {
      left: `calc(${inset} + ${indent})`,
      right: strip,
      width: "auto",
      zIndex,
    };
  }

  const content = `(100% - ${inset}${indent ? ` - ${indent}` : ""} - ${strip})`;
  return {
    left: `calc(${inset}${indent ? ` + ${indent}` : ""} + ${content} * ${columnIndex} / ${columnCount})`,
    right: "auto",
    width: `calc(${content} / ${columnCount} - ${CALENDAR_COLUMN_GAP_PX}px)`,
    zIndex,
  };
}

export function startOfWeekMonday(d: Date): Date {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function formatDayShort(d: Date, locale = "en-GB"): string {
  return capitalizeDateWords(d.toLocaleDateString(locale, { weekday: "short", day: "numeric" }), locale);
}

export function formatMonthYear(d: Date, locale = "en-GB"): string {
  return capitalizeDateWords(d.toLocaleDateString(locale, { month: "long", year: "numeric" }), locale);
}

export function capitalizeDateWords(value: string, locale = "en-GB"): string {
  return value.replace(/(^|\s)(\S)/g, (_, prefix: string, char: string) => {
    return `${prefix}${char.toLocaleUpperCase(locale)}`;
  });
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Current time line position (px from top of grid) or null if outside range. */
export function nowLineOffsetPx(): number | null {
  const n = new Date();
  const mins = n.getHours() * 60 + n.getMinutes();
  const startM = CAL_START_H * 60;
  const endM = (CAL_END_H + 1) * 60;
  if (mins < startM || mins > endM) return null;
  return ((mins - startM) / 60) * HOUR_PX;
}
